import { FirebaseError } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import {
  getStorage,
  ref,
  uploadBytesResumable,
  UploadTask,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

import getExtension from 'common/getExtension';
import {
  authenticationEmitter,
  AuthenticationEvents,
  getUser,
} from 'helpers/auth';
import { User } from 'helpers/auth/user.schema';

type OnStateChangeCallback = (snapshot: UploadTaskSnapshot) => void;

export type UploadFileFunction = (
  file: File,
  onStateChange?: OnStateChangeCallback,
) => Promise<UploadTaskSnapshot>;

// Note that the Firebase API will respond with a the following code
// whether the token is invalid or has just expired. We will interpret
// it to suggest that the token has expired and therefore the user needs
// to log in again.
const INVALID_OR_EXPIRED_ERROR_CODE = 'auth/invalid-custom-token';

// We want to ignore cancel errors as it could be a volunteer operation
// from the user, or from the React component (when unmounting for instance)
const UPLOAD_CANCEL_ERROR_CODE = 'storage/canceled';

/**
 * Returns true if the error is a FirebaseError that should not
 * be handled as a failure, but as something that can normally occur.
 */
export const isExpectedFirebaseError = (
  error: unknown,
): error is FirebaseError => {
  return (
    error instanceof FirebaseError && error.code === UPLOAD_CANCEL_ERROR_CODE
  );
};

const upload = (folder: string, file: File): UploadTask => {
  const fileName = uuidv4();
  const storage = getStorage();

  const refUpload = ref(
    storage,
    `${folder}/${fileName}.${getExtension(file.name)}`,
  );

  const task = uploadBytesResumable(refUpload, file, {
    cacheControl: 'max-age=31556926',
    contentType: file.type,
  });

  return task;
};

const authenticateUser = async (user: User) => {
  if (!user.firebaseToken) {
    throw new Error('Firebase authentication failed: missing token');
  }

  const auth = getAuth();

  try {
    await signInWithCustomToken(auth, user.firebaseToken);
  } catch (error) {
    if (
      error instanceof FirebaseError &&
      error.code === INVALID_OR_EXPIRED_ERROR_CODE
    ) {
      // Starts the process of temporarily locking the app
      authenticationEmitter.emit(AuthenticationEvents.OnUnauthorized);
    }

    throw error;
  }
};

const uploadToUsersNamespace = async (
  folder: string,
  file: File,
  onStateChange?: (snapshot: UploadTaskSnapshot) => void,
): Promise<UploadTaskSnapshot> => {
  const user = getUser();

  if (!user) {
    throw new Error('Upload failed: user not found');
  }

  await authenticateUser(user);

  /**
   * This is a thenable, thus it will be resolved since this is an async function.
   * It means that instead of returning an upload stream, it's actually returning a single snapshot at the end of the upload.
   */
  const task = upload(`users/${user.uid}/${folder}`, file);

  if (onStateChange) {
    task.on('state_changed', onStateChange);
  }

  return task;
};

export const uploadKyc: UploadFileFunction = (...params) =>
  uploadToUsersNamespace('kyc/documents', ...params);

export const uploadReceipt: UploadFileFunction = (...params) =>
  uploadToUsersNamespace('receipts', ...params);

export const getUploadShineStartDocumentFunction =
  (shineStartId: string): UploadFileFunction =>
  (...params) =>
    uploadToUsersNamespace(`shine_starts/${shineStartId}/documents`, ...params);

export const getUploadKybFunction =
  (companyProfileId: string): UploadFileFunction =>
  (...params) => {
    return uploadToUsersNamespace(
      `kyb/${companyProfileId}/documents`,
      ...params,
    );
  };

export const getUploadDirectDebitMandateFunction =
  (uniqueMandateReference: string): UploadFileFunction =>
  (...params) =>
    uploadToUsersNamespace(`b2b_mandates/${uniqueMandateReference}`, ...params);

export const getUploadBulkTransferFunction =
  (companyProfileId: string): UploadFileFunction =>
  (...params) =>
    uploadToUsersNamespace(`bulk_transfers/${companyProfileId}`, ...params);

export const getUploadCompanyLogoFunction =
  (companyProfileId: string): UploadFileFunction =>
  (...params) =>
    uploadToUsersNamespace(`companies/${companyProfileId}/logos`, ...params);

export default upload;
